import React, { useState } from 'react';
import { Settings, ShieldCheck, Database, Bell, Save } from 'lucide-react';

export default function SettingsPage() {
  const [storeName, setStoreName] = useState('Zarin Wear');
  const [supportEmail, setSupportEmail] = useState('concierge@zarinwear.com');
  const [shippingThreshold, setShippingThreshold] = useState('3000');
  const [returnWindow, setReturnWindow] = useState('14');
  const [saved, setSaved] = useState(false);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>System Settings & Infrastructure</h1>
          <p style={styles.subtitle}>Manage atelier policies, Cloudflare bindings, and quota thresholds</p>
        </div>
        <button style={styles.btnPrimary} onClick={handleSave}>
          <Save size={16} />
          <span>{saved ? 'Saved Successfully' : 'Save Changes'}</span>
        </button>
      </div>

      <div style={styles.grid}>
        {/* General Store Settings */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Storefront Configuration</h2>
          <form style={styles.form} onSubmit={handleSave}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Brand Name</label>
              <input
                type="text"
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Concierge Support Email</label>
              <input
                type="email"
                value={supportEmail}
                onChange={(e) => setSupportEmail(e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.formRow2}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Complimentary Shipping Threshold (₹)</label>
                <input
                  type="number"
                  value={shippingThreshold}
                  onChange={(e) => setShippingThreshold(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Return Window (Days)</label>
                <input
                  type="number"
                  value={returnWindow}
                  onChange={(e) => setReturnWindow(e.target.value)}
                  style={styles.input}
                />
              </div>
            </div>
          </form>
        </div>

        {/* Cloudflare Edge Status */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Edge Infrastructure & Cloudflare Health</h2>
          <div style={styles.statusList}>
            <div style={styles.statusRow}>
              <div style={styles.statusLabel}>
                <Database size={16} style={{ color: '#4ADE80' }} />
                <span>Cloudflare D1 (SQLite)</span>
              </div>
              <span style={styles.statusOk}>Healthy (zarin_core)</span>
            </div>

            <div style={styles.statusRow}>
              <div style={styles.statusLabel}>
                <ShieldCheck size={16} style={{ color: '#4ADE80' }} />
                <span>Cloudflare R2 Storage</span>
              </div>
              <span style={styles.statusOk}>Bound (zarin-media)</span>
            </div>

            <div style={styles.statusRow}>
              <div style={styles.statusLabel}>
                <Bell size={16} style={{ color: '#4ADE80' }} />
                <span>Web Push & Queues</span>
              </div>
              <span style={styles.statusOk}>Active (push-fanout)</span>
            </div>
          </div>

          <div style={styles.quotaBox}>
            <h4 style={styles.quotaTitle}>Daily Quota Consumption Monitor (§1.20)</h4>
            <div style={styles.quotaBarWrap}>
              <div style={styles.quotaBarLabel}>
                <span>Workers Requests</span>
                <span>12,410 / 100,000 (12.4%)</span>
              </div>
              <div style={styles.quotaTrack}>
                <div style={{ ...styles.quotaFill, width: '12.4%' }} />
              </div>
            </div>

            <div style={styles.quotaBarWrap}>
              <div style={styles.quotaBarLabel}>
                <span>D1 Database Rows Read</span>
                <span>182,300 / 5,000,000 (3.6%)</span>
              </div>
              <div style={styles.quotaTrack}>
                <div style={{ ...styles.quotaFill, width: '3.6%' }} />
              </div>
            </div>

            <div style={styles.quotaBarWrap}>
              <div style={styles.quotaBarLabel}>
                <span>Daily Transactional Emails (Resend)</span>
                <span>14 / 100 (14%)</span>
              </div>
              <div style={styles.quotaTrack}>
                <div style={{ ...styles.quotaFill, width: '14%' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' },
  title: { fontSize: '1.75rem', fontWeight: 600, color: '#FAF7F2', margin: 0 },
  subtitle: { fontSize: '0.875rem', color: '#9B9B9B', margin: '0.25rem 0 0 0' },
  btnPrimary: {
    background: '#C9A84C',
    color: '#1A1A1A',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
  },
  grid: { display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' },
  card: {
    background: '#1F1F1F',
    border: '1px solid #2C2C2C',
    borderRadius: '8px',
    padding: '1.5rem',
  },
  cardTitle: { color: '#FAF7F2', fontSize: '1.125rem', margin: '0 0 1.25rem 0', fontWeight: 500 },
  form: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  formRow2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' },
  label: { fontSize: '0.75rem', color: '#9B9B9B', fontWeight: 500 },
  input: {
    background: '#141414',
    border: '1px solid #333',
    borderRadius: '4px',
    padding: '0.6rem 0.75rem',
    color: '#FAF7F2',
    fontSize: '0.875rem',
  },
  statusList: { display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' },
  statusRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    background: '#141414',
    borderRadius: '6px',
    border: '1px solid #282828',
  },
  statusLabel: { display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#FAF7F2', fontSize: '0.875rem' },
  statusOk: { color: '#4ADE80', fontSize: '0.75rem', fontWeight: 500 },
  quotaBox: {
    background: '#161616',
    border: '1px solid #282828',
    borderRadius: '6px',
    padding: '1.25rem',
  },
  quotaTitle: { margin: '0 0 1rem 0', color: '#FAF7F2', fontSize: '0.875rem' },
  quotaBarWrap: { display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '0.75rem' },
  quotaBarLabel: { display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#888' },
  quotaTrack: { height: '6px', background: '#262626', borderRadius: '999px', overflow: 'hidden' },
  quotaFill: { height: '100%', background: '#C9A84C', borderRadius: '999px' },
};
